import { EditSessionOp } from '../../../shared/edit-session/edit-session';
import { Territory, TerritoryWithMetrics } from '../../../map/features/territories/territory-feature';

/**
 * The Territories edit session, as plain data.
 *
 * No Angular import on purpose: this is the part with rules in it, and rules are
 * cheaper to pin when a test can call them directly. The component owns HTTP,
 * the map and the form; this owns what an edit MEANS.
 *
 * The server resolves a draft - overlap repair, lead reassignment, disclosure -
 * so nothing here computes geometry. What it does is accumulate the edits into
 * the shape `POST /api/territories/draft/preview` expects, and keep the roster
 * on screen agreeing with them.
 */

/** A territory that does not exist yet carries a client-assigned negative id. */
export type DraftKey = { id: number } | { tempId: number };

export interface DraftState {
  /** The roster as the session believes it: saved rows with edits folded in. */
  rows: TerritoryWithMetrics[];
  /**
   * One entry per touched territory, ordered by when it was last edited.
   *
   * One entry, not one per keystroke: the server applies edits in EditSequence
   * order and the last one wins, so re-editing a patch replaces its entry and
   * moves it to the end. Two entries for one territory would make the earlier
   * one a boundary the admin has already replaced.
   */
  edits: DraftEdit[];
  /** Counts down from -1 so a creation has a stable identity before it is saved. */
  nextTempId: number;
}

export interface DraftEdit {
  id: number | null;
  tempId: number | null;
  editSequence: number;
  name: string | null;
  status: string | null;
  boundaryGeoJson: string | null;
  assignedUserId: number | null;
  depotId: number | null;
  color: string | null;
  /** The version this edit was made against; null for a creation. A mismatch at
   *  commit means somebody else moved the boundary and the whole save is
   *  rejected rather than silently overwriting them. */
  expectedGeometryVersion: number | null;
  deleted: boolean;
}

export type TerritoryFields = Pick<
  DraftEdit, 'name' | 'status' | 'assignedUserId' | 'color'>;

export interface RedrawOp extends EditSessionOp {
  kind: 'redraw';
  key: DraftKey;
  boundaryGeoJson: string;
}

export interface CreateOp extends EditSessionOp {
  kind: 'create';
  boundaryGeoJson: string;
  fields: TerritoryFields;
}

export interface EditFieldsOp extends EditSessionOp {
  kind: 'fields';
  key: DraftKey;
  fields: TerritoryFields;
}

export interface DeleteOp extends EditSessionOp {
  kind: 'delete';
  key: DraftKey;
}

export type DraftOp = RedrawOp | CreateOp | EditFieldsOp | DeleteOp;

export function sameKey(a: DraftKey, b: DraftKey): boolean {
  return 'id' in a && 'id' in b ? a.id === b.id
    : 'tempId' in a && 'tempId' in b ? a.tempId === b.tempId
    : false;
}

export function keyOf(row: TerritoryWithMetrics): DraftKey {
  return row.territory.id < 0 ? { tempId: row.territory.id } : { id: row.territory.id };
}

/** A blank row so a creation appears in the roster before it has been saved. */
function newRow(tempId: number, boundaryGeoJson: string, fields: TerritoryFields): TerritoryWithMetrics {
  const territory: Territory = {
    id: tempId, areaId: tempId, name: fields.name ?? 'New territory',
    status: fields.status ?? 'Draft', boundaryGeoJson, geometryVersion: 0,
    assignedUserId: fields.assignedUserId, depotId: null,
    color: fields.color, source: 'Manual', approvedByUserId: null, approvedAt: null,
    createdAt: '', updatedAt: '',
  };
  // Metrics are the server's to compute; a preview fills them in. Zeroes here
  // are honestly "not counted yet", not a claim that the patch is empty.
  return {
    territory, leads: 0, customers: null, openCases: 0, won: 0, lost: 0,
    openPipelineValue: 0, areaSqKm: 0, density: 0,
  };
}

/**
 * Records that a territory was touched, or updates the record if it already was.
 *
 * Moving the entry to the end is the point: EditSequence is the order the admin
 * worked in, and overlap repair resolves ties by it. Editing Zone 3, then Zone
 * 4, then Zone 3 again must leave Zone 3 last, because that is the shape the
 * admin last saw win.
 */
function touch(
  edits: DraftEdit[],
  key: DraftKey,
  row: TerritoryWithMetrics | undefined,
  change: Partial<DraftEdit>,
): DraftEdit[] {
  const existing = edits.find(e => sameKey(editKey(e), key));
  const rest = edits.filter(e => !sameKey(editKey(e), key));
  const sequence = rest.length === 0 ? 0 : Math.max(...rest.map(e => e.editSequence)) + 1;
  const base: DraftEdit = existing ?? {
    id: 'id' in key ? key.id : null,
    tempId: 'tempId' in key ? key.tempId : null,
    editSequence: sequence,
    name: row?.territory.name ?? null,
    status: row?.territory.status ?? null,
    boundaryGeoJson: null,
    assignedUserId: row?.territory.assignedUserId ?? null,
    depotId: row?.territory.depotId ?? null,
    color: row?.territory.color ?? null,
    // Read from the row the edit was made against, not from the current server
    // state: that is what makes a concurrent change a 409 instead of a
    // silent overwrite.
    expectedGeometryVersion: row && row.territory.id > 0 ? row.territory.geometryVersion : null,
    deleted: false,
  };
  return [...rest, { ...base, ...change, editSequence: sequence }];
}

function editKey(edit: DraftEdit): DraftKey {
  return edit.tempId != null ? { tempId: edit.tempId } : { id: edit.id! };
}

/** Fold one edit into the draft. Pure and total - the session re-runs the whole
 *  list on every undo, so this must not depend on anything outside its
 *  arguments and must not mutate them. */
export function reduceDraft(state: DraftState, op: DraftOp): DraftState {
  if (op.kind === 'create') {
    const tempId = state.nextTempId;
    const row = newRow(tempId, op.boundaryGeoJson, op.fields);
    return {
      rows: [...state.rows, row],
      edits: touch(state.edits, { tempId }, row, {
        tempId, id: null, boundaryGeoJson: op.boundaryGeoJson, ...op.fields,
      }),
      nextTempId: tempId - 1,
    };
  }

  const row = state.rows.find(r => sameKey(keyOf(r), op.key));
  if (!row) return state;

  if (op.kind === 'delete') {
    return {
      ...state,
      rows: state.rows.filter(r => !sameKey(keyOf(r), op.key)),
      edits: touch(state.edits, op.key, row, { deleted: true }),
    };
  }

  const change: Partial<DraftEdit> = op.kind === 'redraw'
    ? { boundaryGeoJson: op.boundaryGeoJson }
    : { ...op.fields };
  // name and status are non-null on the display row but nullable on the wire,
  // where null means "unchanged". Coalescing here keeps the roster showing the
  // last real value instead of blanking a cell the admin only tabbed through.
  const merged: Territory = op.kind === 'redraw'
    ? { ...row.territory, boundaryGeoJson: op.boundaryGeoJson }
    : {
        ...row.territory,
        name: op.fields.name ?? row.territory.name,
        status: op.fields.status ?? row.territory.status,
        assignedUserId: op.fields.assignedUserId,
        color: op.fields.color,
      };

  return {
    ...state,
    rows: state.rows.map(r => sameKey(keyOf(r), op.key) ? { ...r, territory: merged } : r),
    edits: touch(state.edits, op.key, row, change),
  };
}

/**
 * The draft as the API wants it.
 *
 * AddedStops stays empty: creating a lead is the Growth board's job. It is in
 * the payload rather than omitted because the contract is positional.
 */
export function toDraftInput(state: DraftState): {
  territories: DraftEdit[];
  addedStops: never[];
} {
  return {
    territories: [...state.edits].sort((a, b) => a.editSequence - b.editSequence),
    addedStops: [],
  };
}
